import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WheelDateValue } from '../../components/WheelDatePicker';
import {
  fetchTaxDeclarationProgress,
  upsertTaxDeclarationProgress,
} from '../taxDeclarationApi';
import type { MaritalStatus } from '../spouseSection/spouseSectionTypes';
import { parseIncomeYen, formatIncomeYen } from '../spouseSection/spouseEligibility';
import { formatDobDisplay } from '../spouseSection/spouseSectionDisplay';
import {
  employeeIncomeFromProgress,
  fetchHasDisabilityContext,
  fetchSingleParentCandidates,
  spouseChangeReason,
} from './specialStatusApi';
import {
  evaluateSingleParent,
  evaluateWidow,
  evaluateWorkingStudent,
  inferWidowLossFromSpouseChangeReason,
  isSingleParentIncomeIneligible,
  shouldTryNextSingleParentChild,
  singleParentResultMessage,
  usesSingleParentChildLoop,
  widowResultMessage,
  workingStudentResultMessage,
} from './specialStatusEligibility';
import { specialStatusFromApi } from './specialStatusMapper';
import {
  buildResumeState,
  clearFromStepInclusive,
  hasDisplayableAnswer,
  isMarried,
  isSpecialStatusComplete,
  isStepAnswered,
  landOnStepAfterUndo,
  lastAnsweredStep,
  nextStepAfter,
  showUndoForStep,
  specialStatusCursor,
  stepIndex,
  stepQuestionKey,
} from './specialStatusSteps';
import type {
  SingleParentCandidate,
  SingleParentChildHistoryEntry,
  SpecialStatusSnapshot,
  SpecialStatusStep,
  SchoolType,
  WidowSpouseLost,
  YesNo,
} from './specialStatusTypes';
import { emptySpecialStatus, SCHOOL_TYPE_OPTIONS, WIDOW_LOSS_OPTIONS } from './specialStatusTypes';
import { interpolateI18n } from '../../utils/interpolateI18n';

function defaultChangeDateWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
}

function wheelFromIso(iso: string, fallback: WheelDateValue): WheelDateValue {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return fallback;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function isoFromWheel(w: WheelDateValue): string {
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

const SPECIAL_STATUS_STEPS_ORDER: SpecialStatusStep[] = [
  'singleParentIntro',
  'singleParentPickChild',
  'singleParentConfirmChild',
  'singleParentClaimedByOther',
  'singleParentResult',
  'widowIntro',
  'widowLossStatus',
  'widowRemarried',
  'widowResult',
  'workingStudentIntro',
  'workingStudentEnrolled',
  'workingStudentSchoolType',
  'workingStudentSalaryOnly',
  'workingStudentSalaryRevenue',
  'workingStudentNonEmployment',
  'workingStudentNonEmploymentAmount',
  'workingStudentResult',
  'disabilityWsChangedDuringYear',
  'disabilityWsChangeDate',
];

function stepsUpTo(idx: number): SpecialStatusStep[] {
  return SPECIAL_STATUS_STEPS_ORDER.slice(0, Math.max(0, idx));
}

function patchForChild(c: SingleParentCandidate): Partial<SpecialStatusSnapshot> {
  return {
    singleParentChildEmpFamilyId: c.empFamilyId,
    singleParentChildRelationship: c.relationship,
    singleParentChildConfirmed: '',
    singleParentClaimedByOther: '',
    singleParentOutcome: '',
  };
}

function childIndexFromSnap(candidates: SingleParentCandidate[], snap: SpecialStatusSnapshot): number {
  if (snap.singleParentChildEmpFamilyId == null) return 0;
  const idx = candidates.findIndex((c) => c.empFamilyId === snap.singleParentChildEmpFamilyId);
  return idx >= 0 ? idx : 0;
}

function marriedSkippedPatch(maritalStatus: MaritalStatus | '', from: SpecialStatusStep): Partial<SpecialStatusSnapshot> {
  if (!isMarried(maritalStatus)) return {};
  if (from === 'singleParentIntro') return { singleParentOutcome: 'skipped' };
  if (from === 'widowIntro') return { widowOutcome: 'skipped' };
  return {};
}

function applyMarriedSkips(
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  _employeeIncome: number
): SpecialStatusSnapshot {
  const next = { ...snap };
  if (isMarried(maritalStatus)) {
    next.singleParentOutcome = 'skipped';
    next.widowOutcome = 'skipped';
  }
  return next;
}

export function useSpecialStatusFlow(
  email: string | null,
  taxYear: number,
  t: (key: string) => string,
  onSectionDoneChange: (done: boolean) => void,
  onOpenFamilyInfo?: () => void
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(false);
  const [step, setStep] = useState<SpecialStatusStep>('singleParentIntro');
  const [snap, setSnap] = useState<SpecialStatusSnapshot>(emptySpecialStatus());
  const [maritalStatus, setMaritalStatus] = useState<MaritalStatus | ''>('');
  const [employeeIncome, setEmployeeIncome] = useState(0);
  const [candidates, setCandidates] = useState<SingleParentCandidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<SingleParentCandidate | null>(null);
  const [childCursorIndex, setChildCursorIndex] = useState(0);
  const [childAttemptHistory, setChildAttemptHistory] = useState<SingleParentChildHistoryEntry[]>([]);
  const [committed, setCommitted] = useState<Set<SpecialStatusStep>>(() => new Set());
  const [reviewMode, setReviewMode] = useState(false);
  const [hasDisabilityContext, setHasDisabilityContext] = useState(false);
  const [disabilityWsChangeDateWheel, setDisabilityWsChangeDateWheel] =
    useState<WheelDateValue>(defaultChangeDateWheel);
  const onDoneRef = useRef(onSectionDoneChange);
  onDoneRef.current = onSectionDoneChange;

  const reviewModeActive = reviewMode || step === 'review';
  const isComplete = isSpecialStatusComplete(step) && reviewModeActive;
  const routeOpts = useMemo(
    () => ({
      candidateCount: candidates.length,
      needsDisabilityWsTransferDate: hasDisabilityContext,
    }),
    [candidates.length, hasDisabilityContext]
  );

  const markCommitted = useCallback((s: SpecialStatusStep) => {
    setCommitted((prev) => new Set(prev).add(s));
  }, []);

  const applyCommittedThrough = useCallback((target: SpecialStatusStep) => {
    const idx = stepIndex(target);
    setCommitted(new Set(stepsUpTo(Math.max(0, idx))));
  }, []);

  const persist = useCallback(
    async (opts: {
      nextStep?: SpecialStatusStep | null;
      patch?: Partial<SpecialStatusSnapshot>;
      sectionDone?: boolean;
    }) => {
      if (!email?.trim()) return false;
      const merged = { ...snap, ...(opts.patch ?? {}) };
      const nextSnap = applyMarriedSkips(merged, maritalStatus, employeeIncome);
      setSnap(nextSnap);

      const saved = await upsertTaxDeclarationProgress({
        email: email.trim(),
        taxYear,
        ...(opts.nextStep !== undefined ? { currentStepId: specialStatusCursor(opts.nextStep ?? 'review') } : {}),
        ...(opts.sectionDone !== undefined ? { sectionSpecialDone: opts.sectionDone } : {}),
        specialStatus: nextSnap,
      });
      if (!saved) {
        setSaveError(true);
        return false;
      }
      setSaveError(false);
      if (saved.specialStatus) setSnap(specialStatusFromApi(saved.specialStatus));
      if (opts.sectionDone === true) onDoneRef.current(true);
      if (opts.sectionDone === false) onDoneRef.current(false);
      return true;
    },
    [email, employeeIncome, maritalStatus, snap, taxYear]
  );

  const advance = useCallback(
    async (from: SpecialStatusStep, patch: Partial<SpecialStatusSnapshot>) => {
      const merged = { ...snap, ...patch };
      const next = nextStepAfter(from, merged, maritalStatus, routeOpts);
      markCommitted(from);
      if (!next) return;
      setStep(next);
      await persist({ nextStep: next, patch });
    },
    [maritalStatus, markCommitted, persist, routeOpts, snap]
  );

  const advanceToNextSingleParentChild = useCallback(
    async (historyEntry: SingleParentChildHistoryEntry) => {
      const nextIndex = childCursorIndex + 1;
      if (nextIndex >= candidates.length) return false;
      setChildAttemptHistory((prev) => [...prev, historyEntry]);
      setChildCursorIndex(nextIndex);
      const c = candidates[nextIndex];
      setSelectedCandidate(c);
      const patch = patchForChild(c);
      setStep('singleParentConfirmChild');
      await persist({ nextStep: 'singleParentConfirmChild', patch });
      return true;
    },
    [candidates, childCursorIndex, persist]
  );

  const initSingleParentChildLoop = useCallback((cands: SingleParentCandidate[]) => {
    setChildCursorIndex(0);
    setChildAttemptHistory([]);
    if (cands.length >= 1) {
      setSelectedCandidate(cands[0]);
    } else {
      setSelectedCandidate(null);
    }
  }, []);

  useEffect(() => {
    if (step !== 'disabilityWsChangeDate') return;
    setDisabilityWsChangeDateWheel(
      wheelFromIso(snap.disabilityWsChangeDate, defaultChangeDateWheel())
    );
  }, [snap.disabilityWsChangeDate, step]);

  useEffect(() => {
    if (!email?.trim()) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const [progress, cands, disabilityCtx] = await Promise.all([
        fetchTaxDeclarationProgress(email.trim(), taxYear),
        fetchSingleParentCandidates(email.trim()),
        fetchHasDisabilityContext(email.trim()),
      ]);
      if (cancelled) return;
      setCandidates(cands);
      setHasDisabilityContext(disabilityCtx);
      const ms = (progress?.maritalStatus as MaritalStatus) ?? '';
      setMaritalStatus(ms);
      const income = employeeIncomeFromProgress(progress);
      setEmployeeIncome(income);
      const loaded = specialStatusFromApi(progress?.specialStatus);
      let staleMaritalSkips = false;
      if (!isMarried(ms)) {
        if (loaded.singleParentOutcome === 'skipped') {
          loaded.singleParentOutcome = '';
          staleMaritalSkips = true;
        }
        if (loaded.widowOutcome === 'skipped') {
          loaded.widowOutcome = '';
          staleMaritalSkips = true;
        }
      } else {
        if (!loaded.singleParentOutcome) loaded.singleParentOutcome = 'skipped';
        if (!loaded.widowOutcome) loaded.widowOutcome = 'skipped';
      }
      setSnap(loaded);

      const inferredWidow = inferWidowLossFromSpouseChangeReason(spouseChangeReason(progress));
      if (!loaded.widowSpouseLost && inferredWidow) {
        loaded.widowSpouseLost = inferredWidow;
      }

      if (loaded.singleParentChildEmpFamilyId) {
        const c = cands.find((x) => x.empFamilyId === loaded.singleParentChildEmpFamilyId);
        if (c) setSelectedCandidate(c);
      } else if (cands.length >= 1) {
        setSelectedCandidate(cands[0]);
      }

      if (usesSingleParentChildLoop(cands.length)) {
        setChildCursorIndex(childIndexFromSnap(cands, loaded));
        setChildAttemptHistory([]);
      } else {
        initSingleParentChildLoop(cands);
      }

      const sectionDoneFlag = progress?.sectionSpecialDone === true;
      const resume = buildResumeState(
        loaded,
        ms,
        cands.length,
        sectionDoneFlag && !staleMaritalSkips,
        disabilityCtx
      );
      const snapToUse = resume.snap;
      const needsPersist = resume.needsPersist || staleMaritalSkips;
      setSnap(snapToUse);

      if (resume.canReview) {
        setReviewMode(true);
        setStep('review');
        setCommitted(resume.committed);
        if (needsPersist) {
          void upsertTaxDeclarationProgress({
            email: email.trim(),
            taxYear,
            currentStepId: specialStatusCursor('review'),
            sectionSpecialDone: true,
            specialStatus: snapToUse,
          });
        }
      } else {
        setReviewMode(false);
        let resumeStep = resume.resumeStep;
        if (resumeStep === 'singleParentPickChild') resumeStep = 'singleParentConfirmChild';
        setStep(resumeStep);
        setCommitted(resume.committed);
        if (needsPersist) {
          void upsertTaxDeclarationProgress({
            email: email.trim(),
            taxYear,
            currentStepId: specialStatusCursor(resume.resumeStep),
            sectionSpecialDone: false,
            specialStatus: snapToUse,
          });
          onDoneRef.current(false);
        }
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [email, taxYear]);

  const handlePickChild = useCallback(
    async (c: SingleParentCandidate) => {
      setSelectedCandidate(c);
      await advance('singleParentPickChild', {
        singleParentChildEmpFamilyId: c.empFamilyId,
        singleParentChildRelationship: c.relationship,
        singleParentClaimedByOther: c.claimedByOtherIncome ? 'yes' : 'no',
      });
    },
    [advance]
  );

  const handleConfirmChild = useCallback(
    async (v: YesNo) => {
      const c =
        selectedCandidate ??
        candidates[childCursorIndex] ??
        null;
      const patch: Partial<SpecialStatusSnapshot> = {
        singleParentChildConfirmed: v,
      };
      if (v === 'yes' && c) {
        patch.singleParentChildEmpFamilyId = c.empFamilyId;
        patch.singleParentChildRelationship = c.relationship;
        patch.singleParentClaimedByOther = c.claimedByOtherIncome ? 'yes' : 'no';
      }
      const merged = { ...snap, ...patch };
      const multiLoop = usesSingleParentChildLoop(candidates.length);

      if (v === 'no') {
        if (
          multiLoop &&
          c &&
          shouldTryNextSingleParentChild({
            childIndex: childCursorIndex,
            candidateCount: candidates.length,
            employeeIncome,
            confirmed: v,
            claimedByOther: '',
          })
        ) {
          await advanceToNextSingleParentChild({ candidate: c, confirmed: v });
          return;
        }
        if (multiLoop && c) {
          setChildAttemptHistory((prev) => [...prev, { candidate: c, confirmed: v }]);
        }
        markCommitted('singleParentConfirmChild');
        patch.singleParentOutcome = 'ineligible';
        setStep('singleParentResult');
        await persist({ nextStep: 'singleParentResult', patch });
        return;
      }

      if (isSingleParentIncomeIneligible(employeeIncome)) {
        markCommitted('singleParentConfirmChild');
        patch.singleParentOutcome = 'ineligible';
        setStep('singleParentResult');
        await persist({ nextStep: 'singleParentResult', patch });
        return;
      }

      markCommitted('singleParentConfirmChild');
      setStep('singleParentClaimedByOther');
      await persist({ nextStep: 'singleParentClaimedByOther', patch });
    },
    [
      advanceToNextSingleParentChild,
      candidates,
      childCursorIndex,
      employeeIncome,
      markCommitted,
      persist,
      selectedCandidate,
      snap,
    ]
  );

  const handleClaimedByOther = useCallback(
    async (v: YesNo) => {
      const c =
        selectedCandidate ??
        candidates[childCursorIndex] ??
        null;
      const patch: Partial<SpecialStatusSnapshot> = { singleParentClaimedByOther: v };
      const merged = { ...snap, ...patch };
      const outcome = evaluateSingleParent(maritalStatus, merged, employeeIncome);
      const multiLoop = usesSingleParentChildLoop(candidates.length);

      if (outcome === 'eligible') {
        markCommitted('singleParentClaimedByOther');
        patch.singleParentOutcome = outcome;
        setStep('singleParentResult');
        await persist({ nextStep: 'singleParentResult', patch });
        return;
      }

      if (
        multiLoop &&
        c &&
        shouldTryNextSingleParentChild({
          childIndex: childCursorIndex,
          candidateCount: candidates.length,
          employeeIncome,
          confirmed: 'yes',
          claimedByOther: v,
        })
      ) {
        await advanceToNextSingleParentChild({ candidate: c, confirmed: 'yes', claimedByOther: v });
        return;
      }

      if (multiLoop && c) {
        setChildAttemptHistory((prev) => [...prev, { candidate: c, confirmed: 'yes', claimedByOther: v }]);
      }
      markCommitted('singleParentClaimedByOther');
      patch.singleParentOutcome = outcome;
      setStep('singleParentResult');
      await persist({ nextStep: 'singleParentResult', patch });
    },
    [
      advanceToNextSingleParentChild,
      candidates,
      childCursorIndex,
      employeeIncome,
      maritalStatus,
      markCommitted,
      persist,
      selectedCandidate,
      snap,
    ]
  );

  const finishSingleParentResult = useCallback(async () => {
    markCommitted('singleParentResult');
    const outcome =
      snap.singleParentOutcome || evaluateSingleParent(maritalStatus, snap, employeeIncome);
    const next = nextStepAfter('singleParentResult', { ...snap, singleParentOutcome: outcome }, maritalStatus, routeOpts);
    if (!next) return;
    setStep(next);
    await persist({ nextStep: next, patch: { singleParentOutcome: outcome } });
  }, [employeeIncome, maritalStatus, markCommitted, persist, routeOpts, snap]);

  const handleWidowLoss = useCallback(
    async (v: WidowSpouseLost) => {
      const merged = { ...snap, widowSpouseLost: v };
      const next = nextStepAfter('widowLossStatus', merged, maritalStatus, routeOpts);
      markCommitted('widowLossStatus');
      if (next === 'widowResult') {
        const outcome = evaluateWidow(maritalStatus, merged, employeeIncome);
        setStep(next);
        await persist({ nextStep: next, patch: { widowSpouseLost: v, widowOutcome: outcome } });
        return;
      }
      setStep(next!);
      await persist({ nextStep: next!, patch: { widowSpouseLost: v } });
    },
    [employeeIncome, maritalStatus, markCommitted, persist, routeOpts, snap]
  );

  const handleWidowRemarried = useCallback(
    async (v: YesNo) => {
      const merged = { ...snap, widowCurrentlyRemarried: v };
      const outcome = evaluateWidow(maritalStatus, merged, employeeIncome);
      await advance('widowRemarried', { widowCurrentlyRemarried: v, widowOutcome: outcome });
    },
    [advance, employeeIncome, maritalStatus, snap]
  );

  const finishWidowResult = useCallback(async () => {
    markCommitted('widowResult');
    const outcome = snap.widowOutcome || evaluateWidow(maritalStatus, snap, employeeIncome);
    const next = nextStepAfter('widowResult', { ...snap, widowOutcome: outcome }, maritalStatus, routeOpts);
    if (!next) return;
    setStep(next);
    await persist({ nextStep: next, patch: { widowOutcome: outcome } });
  }, [employeeIncome, maritalStatus, markCommitted, persist, routeOpts, snap]);

  const handleWorkingStudentEnrolled = useCallback(
    async (v: YesNo) => {
      const merged = { ...snap, workingStudentEnrolled: v };
      const next = nextStepAfter('workingStudentEnrolled', merged, maritalStatus, routeOpts);
      markCommitted('workingStudentEnrolled');
      if (v === 'no') {
        const outcome = 'ineligible' as const;
        setStep('workingStudentResult');
        await persist({
          nextStep: 'workingStudentResult',
          patch: { workingStudentEnrolled: v, workingStudentOutcome: outcome },
        });
        return;
      }
      setStep(next!);
      await persist({ nextStep: next!, patch: { workingStudentEnrolled: v } });
    },
    [maritalStatus, markCommitted, persist, routeOpts, snap]
  );

  const handleSchoolType = useCallback(
    async (v: SchoolType) => {
      await advance('workingStudentSchoolType', { workingStudentSchoolType: v });
    },
    [advance]
  );

  const handleSalaryOnly = useCallback(
    async (v: YesNo) => {
      await advance('workingStudentSalaryOnly', { workingStudentSalaryOnly: v });
    },
    [advance]
  );

  const handleSalaryRevenue = useCallback(async () => {
    if (parseIncomeYen(snap.workingStudentSalaryRevenue) <= 0) return;
    await advance('workingStudentSalaryRevenue', {});
  }, [advance, snap.workingStudentSalaryRevenue]);

  const handleNonEmployment = useCallback(
    async (v: YesNo) => {
      const merged = { ...snap, workingStudentHasNonEmploymentIncome: v };
      const next = nextStepAfter('workingStudentNonEmployment', merged, maritalStatus, routeOpts);
      markCommitted('workingStudentNonEmployment');
      if (next === 'workingStudentResult') {
        const outcome = evaluateWorkingStudent(merged, employeeIncome);
        setStep(next);
        await persist({
          nextStep: next,
          patch: { workingStudentHasNonEmploymentIncome: v, workingStudentOutcome: outcome },
        });
        return;
      }
      setStep(next!);
      await persist({ nextStep: next!, patch: { workingStudentHasNonEmploymentIncome: v } });
    },
    [employeeIncome, maritalStatus, markCommitted, persist, routeOpts, snap]
  );

  const handleNonEmploymentAmount = useCallback(async () => {
    const merged = { ...snap };
    const outcome = evaluateWorkingStudent(merged, employeeIncome);
    await advance('workingStudentNonEmploymentAmount', { workingStudentOutcome: outcome });
  }, [advance, employeeIncome, snap]);

  const finishWorkingStudentResult = useCallback(async () => {
    markCommitted('workingStudentResult');
    const outcome = snap.workingStudentOutcome || evaluateWorkingStudent(snap, employeeIncome);
    const merged = { ...snap, workingStudentOutcome: outcome };
    const next = nextStepAfter('workingStudentResult', merged, maritalStatus, routeOpts);
    if (!next) return;
    if (next === 'disabilityWsChangedDuringYear') {
      setReviewMode(false);
      setStep(next);
      await persist({
        nextStep: next,
        sectionDone: false,
        patch: { workingStudentOutcome: outcome },
      });
      return;
    }
    const resume = buildResumeState(
      merged,
      maritalStatus,
      candidates.length,
      true,
      hasDisabilityContext
    );
    setReviewMode(true);
    setStep('review');
    setCommitted(resume.committed);
    await persist({ nextStep: 'review', sectionDone: true, patch: { workingStudentOutcome: outcome } });
  }, [
    candidates.length,
    employeeIncome,
    hasDisabilityContext,
    maritalStatus,
    markCommitted,
    persist,
    routeOpts,
    snap,
  ]);

  const handleDisabilityWsChangedDuringYear = useCallback(
    async (v: YesNo) => {
      const patch: Partial<SpecialStatusSnapshot> = {
        disabilityWsChangedDuringYear: v,
        ...(v === 'no' ? { disabilityWsChangeDate: '' } : {}),
      };
      const merged = { ...snap, ...patch };
      markCommitted('disabilityWsChangedDuringYear');
      const next = nextStepAfter('disabilityWsChangedDuringYear', merged, maritalStatus, routeOpts);
      if (!next) return;
      if (next === 'review') {
        const resume = buildResumeState(
          merged,
          maritalStatus,
          candidates.length,
          true,
          hasDisabilityContext
        );
        setReviewMode(true);
        setStep('review');
        setCommitted(resume.committed);
        await persist({ nextStep: 'review', sectionDone: true, patch });
        return;
      }
      setReviewMode(false);
      setStep(next);
      await persist({ nextStep: next, sectionDone: false, patch });
    },
    [candidates.length, hasDisabilityContext, maritalStatus, markCommitted, persist, routeOpts, snap]
  );

  const handleDisabilityWsChangeDate = useCallback(
    async (iso: string) => {
      if (!iso.trim()) return;
      const patch: Partial<SpecialStatusSnapshot> = { disabilityWsChangeDate: iso.trim() };
      const merged = { ...snap, ...patch };
      markCommitted('disabilityWsChangeDate');
      const resume = buildResumeState(
        merged,
        maritalStatus,
        candidates.length,
        true,
        hasDisabilityContext
      );
      setReviewMode(true);
      setStep('review');
      setCommitted(resume.committed);
      await persist({ nextStep: 'review', sectionDone: true, patch });
    },
    [candidates.length, hasDisabilityContext, maritalStatus, markCommitted, persist, snap]
  );

  const handleSendDisabilityWsChangeDate = useCallback(async () => {
    await handleDisabilityWsChangeDate(isoFromWheel(disabilityWsChangeDateWheel));
  }, [disabilityWsChangeDateWheel, handleDisabilityWsChangeDate]);

  const enterReviewFromMenu = useCallback(() => {
    if (!isStepAnswered('workingStudentResult', snap, maritalStatus, candidates.length)) return;
    const resume = buildResumeState(snap, maritalStatus, candidates.length, true, hasDisabilityContext);
    if (!resume.canReview) return;
    setReviewMode(true);
    setStep('review');
    setCommitted(resume.committed);
  }, [candidates.length, hasDisabilityContext, maritalStatus, snap]);

  const skipIntroToNext = useCallback(
    async (from: SpecialStatusStep) => {
      markCommitted(from);
      let patch = marriedSkippedPatch(maritalStatus, from);
      if (from === 'singleParentIntro' && candidates.length >= 1) {
        initSingleParentChildLoop(candidates);
        patch = { ...patch, ...patchForChild(candidates[0]) };
      }
      const merged = { ...snap, ...patch };
      if (Object.keys(patch).length > 0) setSnap(merged);
      const next = nextStepAfter(from, merged, maritalStatus, routeOpts);
      if (!next) return;
      setStep(next);
      await persist({ nextStep: next, patch: Object.keys(patch).length > 0 ? patch : undefined });
    },
    [candidates, initSingleParentChildLoop, maritalStatus, markCommitted, persist, routeOpts, snap]
  );

  const handleUndo = useCallback(
    async (undoFromStep?: SpecialStatusStep) => {
      const undoFrom =
        undoFromStep ??
        (step === 'review'
          ? lastAnsweredStep(snap, maritalStatus, candidates.length, hasDisabilityContext) ??
            'workingStudentResult'
          : null);
      if (!undoFrom) return;

      const landOn = landOnStepAfterUndo(undoFrom, snap, maritalStatus, routeOpts);
      if (!landOn) return;

      const clearPatch = clearFromStepInclusive(undoFrom, snap, maritalStatus, routeOpts);
      const merged = applyMarriedSkips({ ...snap, ...clearPatch }, maritalStatus, employeeIncome);

      onDoneRef.current(false);
      setReviewMode(false);
      setStep(landOn);
      applyCommittedThrough(landOn);
      setSnap(merged);

      await persist({ nextStep: landOn, sectionDone: false, patch: clearPatch });
    },
    [
      applyCommittedThrough,
      candidates.length,
      employeeIncome,
      hasDisabilityContext,
      maritalStatus,
      persist,
      routeOpts,
      snap,
      step,
    ]
  );

  const canShowUndoForStep = useCallback(
    (s: SpecialStatusStep) =>
      showUndoForStep(s, step, snap, maritalStatus, committed.has(s), reviewModeActive, {
        candidateCount: candidates.length,
        allCommitted: committed,
        needsDisabilityWsTransferDate: hasDisabilityContext,
      }),
    [candidates.length, committed, hasDisabilityContext, maritalStatus, reviewModeActive, snap, step]
  );

  const userAnswerForStep = useCallback(
    (s: SpecialStatusStep): string => {
      switch (s) {
        case 'singleParentPickChild': {
          const c =
            selectedCandidate ??
            candidates.find((x) => x.empFamilyId === snap.singleParentChildEmpFamilyId);
          return c
            ? `${c.fullName} (${t(`submitDocuments.familyInfo.relationship.${c.relationship}`)})`
            : '—';
        }
        case 'singleParentConfirmChild':
          return selectedCandidate
            ? `${selectedCandidate.fullName} (${t(`submitDocuments.familyInfo.relationship.${selectedCandidate.relationship}`)}) — ${t(
                snap.singleParentChildConfirmed === 'yes'
                  ? 'submitDocuments.dependentDeduction.specialStatus.yes'
                  : 'submitDocuments.dependentDeduction.specialStatus.no'
              )}`
            : t('submitDocuments.dependentDeduction.specialStatus.no');
        case 'singleParentClaimedByOther':
          return t(
            snap.singleParentClaimedByOther === 'yes'
              ? 'submitDocuments.dependentDeduction.specialStatus.yes'
              : 'submitDocuments.dependentDeduction.specialStatus.no'
          );
        case 'singleParentResult':
          return singleParentResultMessage(snap.singleParentOutcome, employeeIncome, t);
        case 'widowLossStatus':
          return snap.widowSpouseLost
            ? t(`submitDocuments.dependentDeduction.specialStatus.widow.loss.${snap.widowSpouseLost}`)
            : '—';
        case 'widowRemarried':
          return t(
            snap.widowCurrentlyRemarried === 'yes'
              ? 'submitDocuments.dependentDeduction.specialStatus.yes'
              : 'submitDocuments.dependentDeduction.specialStatus.no'
          );
        case 'widowResult':
          return widowResultMessage(snap.widowOutcome, employeeIncome, t);
        case 'workingStudentEnrolled':
          return t(
            snap.workingStudentEnrolled === 'yes'
              ? 'submitDocuments.dependentDeduction.specialStatus.yes'
              : snap.workingStudentEnrolled === 'no'
                ? 'submitDocuments.dependentDeduction.specialStatus.no'
                : '—'
          );
        case 'workingStudentSalaryOnly':
          return t(
            snap.workingStudentSalaryOnly === 'yes'
              ? 'submitDocuments.dependentDeduction.specialStatus.yes'
              : snap.workingStudentSalaryOnly === 'no'
                ? 'submitDocuments.dependentDeduction.specialStatus.no'
                : '—'
          );
        case 'workingStudentNonEmployment':
          return t(
            snap.workingStudentHasNonEmploymentIncome === 'yes'
              ? 'submitDocuments.dependentDeduction.specialStatus.yes'
              : snap.workingStudentHasNonEmploymentIncome === 'no'
                ? 'submitDocuments.dependentDeduction.specialStatus.no'
                : '—'
          );
        case 'workingStudentSchoolType':
          return snap.workingStudentSchoolType
            ? t(
                `submitDocuments.dependentDeduction.specialStatus.workingStudent.schoolType.${snap.workingStudentSchoolType}`
              )
            : '—';
        case 'workingStudentSalaryRevenue':
          return snap.workingStudentSalaryRevenue
            ? `${formatIncomeYen(parseIncomeYen(snap.workingStudentSalaryRevenue))} yen`
            : '—';
        case 'workingStudentNonEmploymentAmount':
          return snap.workingStudentNonEmploymentIncome
            ? `${formatIncomeYen(parseIncomeYen(snap.workingStudentNonEmploymentIncome))} yen`
            : '—';
        case 'workingStudentResult':
          return workingStudentResultMessage(snap.workingStudentOutcome, t);
        case 'disabilityWsChangedDuringYear':
          return t(
            snap.disabilityWsChangedDuringYear === 'yes'
              ? 'submitDocuments.dependentDeduction.specialStatus.yes'
              : snap.disabilityWsChangedDuringYear === 'no'
                ? 'submitDocuments.dependentDeduction.specialStatus.no'
                : '—'
          );
        case 'disabilityWsChangeDate':
          return formatDobDisplay(snap.disabilityWsChangeDate);
        default:
          return '';
      }
    },
    [employeeIncome, candidates, selectedCandidate, snap, t]
  );

  const formatChildHistoryAnswer = useCallback(
    (entry: SingleParentChildHistoryEntry, field: 'confirmed' | 'claimedByOther'): string => {
      const c = entry.candidate;
      const name = `${c.fullName} (${t(`submitDocuments.familyInfo.relationship.${c.relationship}`)})`;
      if (field === 'confirmed') {
        return `${name} — ${t(
          entry.confirmed === 'yes'
            ? 'submitDocuments.dependentDeduction.specialStatus.yes'
            : 'submitDocuments.dependentDeduction.specialStatus.no'
        )}`;
      }
      if (entry.claimedByOther == null) return '';
      return t(
        entry.claimedByOther === 'yes'
          ? 'submitDocuments.dependentDeduction.specialStatus.yes'
          : 'submitDocuments.dependentDeduction.specialStatus.no'
      );
    },
    [t]
  );

  const childProgressLabel = useCallback((): string | null => {
    if (!usesSingleParentChildLoop(candidates.length)) return null;
    const c = selectedCandidate ?? candidates[childCursorIndex];
    if (!c) return null;
    return interpolateI18n(t('submitDocuments.dependentDeduction.specialStatus.singleParent.childProgress'), {
      current: String(childCursorIndex + 1),
      total: String(candidates.length),
      name: c.fullName,
      relationship: t(`submitDocuments.familyInfo.relationship.${c.relationship}`),
    });
  }, [candidates, childCursorIndex, selectedCandidate, t]);

  return {
    t,
    step,
    snap,
    setSnap,
    maritalStatus,
    employeeIncome,
    candidates,
    selectedCandidate,
    childAttemptHistory,
    childProgressLabel,
    formatChildHistoryAnswer,
    usesSingleParentChildLoop: usesSingleParentChildLoop(candidates.length),
    committed,
    isLoading,
    saveError,
    reviewMode: reviewModeActive,
    isComplete,
    onOpenFamilyInfo,
    hasDisabilityContext,
    disabilityWsChangeDateWheel,
    setDisabilityWsChangeDateWheel,
    markCommitted,
    skipIntroToNext,
    handleUndo,
    canShowUndoForStep,
    undoLabel: t('submitDocuments.chat.undo'),
    handlePickChild,
    handleConfirmChild,
    handleClaimedByOther,
    finishSingleParentResult,
    handleWidowLoss,
    handleWidowRemarried,
    finishWidowResult,
    handleWorkingStudentEnrolled,
    handleSchoolType,
    handleSalaryOnly,
    handleSalaryRevenue,
    handleNonEmployment,
    handleNonEmploymentAmount,
    finishWorkingStudentResult,
    handleDisabilityWsChangedDuringYear,
    handleDisabilityWsChangeDate,
    handleSendDisabilityWsChangeDate,
    enterReviewFromMenu,
    userAnswerForStep,
    stepQuestionKey,
    schoolTypeOptions: SCHOOL_TYPE_OPTIONS,
    widowLossOptions: WIDOW_LOSS_OPTIONS,
    singleParentResultMessage: () => singleParentResultMessage(snap.singleParentOutcome, employeeIncome, t),
    widowResultMessage: () => widowResultMessage(snap.widowOutcome, employeeIncome, t),
    workingStudentResultMessage: () => workingStudentResultMessage(snap.workingStudentOutcome, t),
    canSendSalaryRevenue: parseIncomeYen(snap.workingStudentSalaryRevenue) > 0,
    canSendNonEmploymentAmount: parseIncomeYen(snap.workingStudentNonEmploymentIncome) > 0,
    isCommitted: (s: SpecialStatusStep) => committed.has(s),
    hasDisplayableAnswer: (s: SpecialStatusStep) =>
      hasDisplayableAnswer(s, snap, maritalStatus, candidates.length),
  };
}

export type SpecialStatusFlow = ReturnType<typeof useSpecialStatusFlow>;
